/** @jsxImportSource solid-js */
import { createEffect, createSignal, on, onCleanup, onMount, type JSX } from "solid-js";
import { clampDraggedPosition, placeBubble } from "../utils/widget-bubble-position";
import { popIn } from "../utils/widget-motion";

export type WidgetBubbleProps = {
  /** Pin anchor in viewport coordinates. */
  x: number;
  y: number;
  setEl?: (el: HTMLElement) => void;
  /** Receives the grip pointerdown handler so cards can place their own grip. */
  setDragStart?: (start: (e: PointerEvent) => void) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: JSX.Element;
};

/**
 * Positioning shell for anything anchored to a pin: measures itself, flips to
 * the side of the pin that fits the viewport, and pops in from that edge.
 * A grip in the top-right corner lets the reviewer drag the bubble aside when
 * it covers the thing they are commenting on.
 */
export function WidgetBubble(props: WidgetBubbleProps): JSX.Element {
  let ref: HTMLDivElement | undefined;
  const [box, setBox] = createSignal<{ left: number; top: number } | null>(null);
  const [drag, setDrag] = createSignal({ x: 0, y: 0 });

  // offsetWidth/Height ignore transforms, so measuring stays correct even
  // mid-entrance-spring and after the autosizing textarea grows the card.
  const measure = (): { width: number; height: number } => ({
    width: ref?.offsetWidth || 300,
    height: ref?.offsetHeight || 140,
  });

  const place = (): void => {
    if (!ref) return;
    const size = measure();
    const placed = placeBubble(
      props.x,
      props.y,
      size.width,
      size.height,
      window.innerWidth,
      window.innerHeight,
    );
    ref.style.transformOrigin = placed.origin;
    setBox({ left: placed.left, top: placed.top });
  };

  const onGripPointerDown = (event: PointerEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    const grip = event.currentTarget as HTMLButtonElement;
    grip.setPointerCapture(event.pointerId);
    const startPointer = { x: event.clientX, y: event.clientY };
    const startDrag = drag();

    const size = measure();
    const clampedDrag = (dx: number, dy: number): { x: number; y: number } => {
      const base = box();
      if (!base) return { x: dx, y: dy };
      const clamped = clampDraggedPosition(
        base.left + dx,
        base.top + dy,
        size.width,
        size.height,
        window.innerWidth,
        window.innerHeight,
      );
      return { x: clamped.left - base.left, y: clamped.top - base.top };
    };

    const onPointerMove = (moveEvent: PointerEvent): void => {
      setDrag(
        clampedDrag(
          startDrag.x + (moveEvent.clientX - startPointer.x),
          startDrag.y + (moveEvent.clientY - startPointer.y),
        ),
      );
    };
    const onPointerUp = (upEvent: PointerEvent): void => {
      grip.releasePointerCapture(upEvent.pointerId);
      grip.removeEventListener("pointermove", onPointerMove);
      grip.removeEventListener("pointerup", onPointerUp);
      grip.removeEventListener("pointercancel", onPointerUp);
    };
    grip.addEventListener("pointermove", onPointerMove);
    grip.addEventListener("pointerup", onPointerUp);
    grip.addEventListener("pointercancel", onPointerUp);
  };

  onMount(() => {
    place();
    props.setDragStart?.(onGripPointerDown);
    if (ref) {
      props.setEl?.(ref);
      popIn(ref, { from: "translateY(6px) scale(0.95)" });
    }
    window.addEventListener("resize", place);
    onCleanup(() => window.removeEventListener("resize", place));
  });

  createEffect(on(() => [props.x, props.y] as const, place, { defer: true }));

  return (
    <div
      ref={ref}
      class="z-pl fixed w-[300px] max-w-[calc(100vw-1.5rem)] font-sans"
      style={{
        left: `${(box()?.left ?? props.x) + drag().x}px`,
        top: `${(box()?.top ?? props.y) + drag().y}px`,
        visibility: box() ? "visible" : "hidden",
      }}
      onMouseEnter={() => props.onMouseEnter?.()}
      onMouseLeave={() => props.onMouseLeave?.()}
    >
      {props.children}
    </div>
  );
}
